import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { auth } from '../firebase';
import { api } from '../api/client';
import { StatCard, LoadingState, ErrorState, PageHeader, Table, Th, Td } from '../components/ui';
import { FeedbackFiltersBar } from './feedback/FeedbackFilters';
import type { FeedbackFilters } from './feedback/FeedbackFilters';
import { FeedbackSummary } from './feedback/FeedbackSummary';
import { FeedbackGrid } from './feedback/FeedbackGrid';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SummaryData {
  dates: string[];
  dau: number[];
  events: number[];
  sessions: number[];
  uniqueEntities: number[];
}

interface FeatureItem {
  eventName: string;
  count: number;
  category: string;
  avgDurationMs?: number;
}

interface FeaturesData {
  features: FeatureItem[];
}

interface EntityItem {
  entityId: string;
  dau: number;
  events: number;
  sessions: number;
  lastActive: string;
}

interface EntitiesData {
  entities: EntityItem[];
}

interface FeedbackSummaryData {
  totalResponses: number;
  avgNps: number | null;
  npsDistribution: { promoters: number; passives: number; detractors: number };
  avgFeatureRating: number | null;
  usefulRatio: number | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CATEGORY_COLORS: Record<string, string> = {
  page_view: '#3b82f6',
  action: '#22c55e',
  feature_use: '#a855f7',
  abandonment: '#f59e0b',
  other: '#6b7280',
};

function avg(arr: number[]): number {
  if (arr.length === 0) return 0;
  return Math.round(arr.reduce((a, b) => a + b, 0) / arr.length);
}

function sum(arr: number[]): number {
  return arr.reduce((a, b) => a + b, 0);
}

function formatDate(iso: string): string {
  return iso.slice(5); // MM-DD
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.round((ms % 60000) / 1000);
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

const BASE_URL = import.meta.env.VITE_ADMIN_API_BASE_URL ?? 'http://localhost:8081/admin';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

function buildExportParams(filters: FeedbackFilters): string {
  const p = new URLSearchParams();
  p.set('days', String(filters.days));
  if (filters.feedbackType) p.set('feedback_type', filters.feedbackType);
  if (filters.role) p.set('role', filters.role);
  if (filters.entityId) p.set('entity_id', filters.entityId);
  return p.toString();
}

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------

type Tab = 'analytics' | 'feedback';

const TAB_LABELS: { id: Tab; label: string }[] = [
  { id: 'analytics', label: 'Analytics' },
  { id: 'feedback', label: 'Feedback' },
];

// ---------------------------------------------------------------------------
// Analytics tab content
// ---------------------------------------------------------------------------

interface AnalyticsFilters {
  days: 7 | 14 | 30 | 90;
  role: string;
  entityId: string;
  category: string;
}

function AnalyticsFiltersBar({ value, onChange }: { value: AnalyticsFilters; onChange: (f: AnalyticsFilters) => void }) {
  const selectCls =
    'bg-gray-900 border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-xs text-white ' +
    'focus:outline-none focus:border-primary/50 transition-colors';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={value.days} onChange={e => onChange({ ...value, days: Number(e.target.value) as AnalyticsFilters['days'] })} className={selectCls}>
        <option value={7}>Last 7 days</option>
        <option value={14}>Last 14 days</option>
        <option value={30}>Last 30 days</option>
        <option value={90}>Last 90 days</option>
      </select>
      <select value={value.role} onChange={e => onChange({ ...value, role: e.target.value })} className={selectCls}>
        <option value="">All Roles</option>
        <option value="executive">executive</option>
        <option value="product_manager">product_manager</option>
        <option value="team_lead">team_lead</option>
        <option value="engineer">engineer</option>
        <option value="designer">designer</option>
        <option value="sales">sales</option>
        <option value="marketing">marketing</option>
        <option value="viewer">viewer</option>
        <option value="admin">admin (no functional role)</option>
        <option value="member">member (no functional role)</option>
      </select>
      <input
        type="text"
        defaultValue={value.entityId}
        placeholder="Entity ID..."
        onChange={e => onChange({ ...value, entityId: e.target.value })}
        className={`${selectCls} w-36 placeholder:text-gray-400`}
      />
      <select value={value.category} onChange={e => onChange({ ...value, category: e.target.value })} className={selectCls}>
        <option value="">All Categories</option>
        <option value="page_view">page_view</option>
        <option value="feature_use">feature_use</option>
        <option value="action">action</option>
        <option value="abandonment">abandonment</option>
        <option value="error">error</option>
      </select>
    </div>
  );
}

function AnalyticsTab() {
  const [filters, setFilters] = useState<AnalyticsFilters>({ days: 7, role: '', entityId: '', category: '' });
  const { days, role, entityId, category } = filters;
  const queryClient = useQueryClient();
  const [isRollingUp, setIsRollingUp] = useState(false);
  const [rollupMsg, setRollupMsg] = useState<string | null>(null);

  const handleRollup = async () => {
    setIsRollingUp(true);
    setRollupMsg(null);
    try {
      const result = await api.post<{ date: string; processed: number }>('/telemetry/rollup-now');
      setRollupMsg(`Rolled up ${result.processed} entities for ${result.date}`);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'telemetry'] });
    } catch {
      setRollupMsg('Rollup failed — check logs');
    } finally {
      setIsRollingUp(false);
    }
  };

  function qs(base: number) {
    const p = new URLSearchParams({ days: String(base) });
    if (role) p.set('role', role);
    if (entityId) p.set('entity_id', entityId);
    if (category) p.set('category', category);
    return p.toString();
  }

  const summaryShort = useQuery<SummaryData>({
    queryKey: ['admin', 'telemetry', 'summary', days, role, entityId, category],
    queryFn: () => api.get<SummaryData>(`/telemetry/summary?${qs(days)}`),
  });

  const summaryTrend = useQuery<SummaryData>({
    queryKey: ['admin', 'telemetry', 'summary', 30, role, entityId, category],
    queryFn: () => api.get<SummaryData>(`/telemetry/summary?${qs(30)}`),
  });

  const features = useQuery<FeaturesData>({
    queryKey: ['admin', 'telemetry', 'features', days, role, entityId, category],
    queryFn: () => api.get<FeaturesData>(`/telemetry/features?${qs(days)}`),
  });

  const entities = useQuery<EntitiesData>({
    queryKey: ['admin', 'telemetry', 'entities', days, role, entityId, category],
    queryFn: () => api.get<EntitiesData>(`/telemetry/entities/summary?${qs(days)}`),
  });

  const isLoading =
    summaryShort.isLoading || summaryTrend.isLoading || features.isLoading || entities.isLoading;
  const hasError =
    summaryShort.isError || summaryTrend.isError || features.isError || entities.isError;

  const kpiDau = summaryShort.data ? avg(summaryShort.data.dau) : 0;
  const kpiEvents = summaryShort.data ? sum(summaryShort.data.events) : 0;
  const kpiSessions = summaryShort.data ? sum(summaryShort.data.sessions) : 0;
  const kpiEntities = summaryShort.data
    ? Math.max(...summaryShort.data.uniqueEntities.filter(v => v > 0), 0)
    : 0;

  const trendData = summaryTrend.data
    ? summaryTrend.data.dates.map((date, i) => ({
        date: formatDate(date),
        DAU: summaryTrend.data!.dau[i],
        Events: summaryTrend.data!.events[i],
      }))
    : [];

  const allFeatures = features.data?.features ?? [];

  const featureData = allFeatures
    .filter(f => !category || f.category === category)
    .slice(0, 20)
    .map(f => ({
      name: f.eventName.length > 32 ? f.eventName.slice(0, 32) : f.eventName,
      count: f.count,
      category: f.category,
    }));

  // Category summary: total events per category across all unfiltered features
  const categorySummary = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const f of allFeatures) {
      totals[f.category] = (totals[f.category] ?? 0) + f.count;
    }
    return Object.entries(totals).sort((a, b) => b[1] - a[1]);
  }, [allFeatures]);

  // Event breakdown grouped by category
  const eventBreakdown = useMemo(() => {
    const grouped: Record<string, FeatureItem[]> = {};
    for (const f of allFeatures) {
      if (!grouped[f.category]) grouped[f.category] = [];
      grouped[f.category].push(f);
    }
    // Sort each group by count desc
    for (const cat of Object.keys(grouped)) {
      grouped[cat].sort((a, b) => b.count - a.count);
    }
    return grouped;
  }, [allFeatures]);

  const [eventBreakdownOpen, setEventBreakdownOpen] = useState(false);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <AnalyticsFiltersBar value={filters} onChange={setFilters} />
        <div className="flex items-center gap-2">
          {rollupMsg && (
            <span className="text-xs text-gray-400">{rollupMsg}</span>
          )}
          <button
            onClick={() => void handleRollup()}
            disabled={isRollingUp}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.06] border border-white/[0.08] text-xs text-gray-300 hover:bg-white/[0.10] hover:text-white transition-colors disabled:opacity-50"
          >
            {isRollingUp ? 'Running…' : 'Run Rollup Now'}
          </button>
        </div>
      </div>

      {isLoading && <LoadingState />}
      {!isLoading && hasError && <ErrorState message="Error loading telemetry data" />}

      {!isLoading && !hasError && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard label="Avg DAU" value={kpiDau} sub={`last ${filters.days}d average`} />
            <StatCard label="Total Events" value={kpiEvents.toLocaleString()} sub={`last ${filters.days}d sum`} />
            <StatCard label="Active Entities" value={kpiEntities} sub={`last ${filters.days}d`} />
            <StatCard label="Total Sessions" value={kpiSessions.toLocaleString()} sub={`last ${filters.days}d sum`} />
          </div>

          {/* DAU / Events trend */}
          <div className="mb-6">
            <h2 className="text-sm font-medium text-gray-400 mb-2">DAU &amp; Events Trend (30 days)</h2>
            <div className="bg-bg-surface rounded-xl border border-white/[0.06] p-4">
              {trendData.length === 0 ? (
                <p className="text-xs text-gray-500 py-6 text-center">No trend data available.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={trendData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#6b7280', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      interval={6}
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fill: '#6b7280', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fill: '#6b7280', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={48}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a2e',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '8px',
                        fontSize: 12,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#9ca3af' }} />
                    <Line yAxisId="left" type="monotone" dataKey="DAU" stroke="#3b82f6" dot={false} strokeWidth={2} />
                    <Line yAxisId="right" type="monotone" dataKey="Events" stroke="#a855f7" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Feature adoption */}
          <div className="mb-6">
            <h2 className="text-sm font-medium text-gray-400 mb-2">
              Feature Adoption — Top 20 Events (last {filters.days}d)
            </h2>
            <div className="bg-bg-surface rounded-xl border border-white/[0.06] p-4">
              {featureData.length === 0 ? (
                <p className="text-xs text-gray-500 py-6 text-center">No feature data available.</p>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(featureData.length * 28, 200)}>
                  <BarChart layout="vertical" data={featureData} margin={{ top: 0, right: 24, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                    <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={200}
                      tick={{ fill: '#9ca3af', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a2e',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '8px',
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {featureData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CATEGORY_COLORS[entry.category] ?? CATEGORY_COLORS.other}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div className="flex flex-wrap gap-3 mt-3">
                {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
                  <div key={cat} className="flex items-center gap-1.5 text-xs text-gray-400">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
                    {cat.replace('_', ' ')}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Category summary row */}
          {categorySummary.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-medium text-gray-400 mb-2">Events by Category (last {filters.days}d)</h2>
              <div className="flex flex-wrap gap-2">
                {categorySummary.map(([cat, total]) => (
                  <div
                    key={cat}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-bg-surface border border-white/[0.06] text-xs"
                  >
                    <span
                      className="inline-block h-2 w-2 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: CATEGORY_COLORS[cat] ?? CATEGORY_COLORS.other }}
                    />
                    <span className="text-gray-300">{cat.replace('_', ' ')}</span>
                    <span className="text-gray-500">{total.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Entity engagement table */}
          <div className="mb-6">
            <h2 className="text-sm font-medium text-gray-400 mb-2">Entity Engagement (last {filters.days}d)</h2>
            {(entities.data?.entities ?? []).length === 0 ? (
              <p className="text-xs text-gray-500">No entity data available.</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Entity ID</Th>
                    <Th>DAU</Th>
                    <Th>Events</Th>
                    <Th>Sessions</Th>
                    <Th>Last Active</Th>
                  </tr>
                </thead>
                <tbody>
                  {(entities.data?.entities ?? []).map(e => (
                    <tr key={e.entityId} className="hover:bg-white/[0.02] transition-colors">
                      <Td>
                        <span className="font-mono text-xs text-gray-300">{e.entityId.slice(0, 8)}</span>
                      </Td>
                      <Td className="font-medium">{e.dau}</Td>
                      <Td>{e.events.toLocaleString()}</Td>
                      <Td>{e.sessions.toLocaleString()}</Td>
                      <Td className="text-gray-400">{e.lastActive}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {/* Event Breakdown (collapsible) */}
          {Object.keys(eventBreakdown).length > 0 && (
            <div className="mb-6">
              <button
                type="button"
                onClick={() => setEventBreakdownOpen(v => !v)}
                className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors mb-2"
              >
                <span>{eventBreakdownOpen ? '▾' : '▸'}</span>
                Event Breakdown by Category
              </button>
              {eventBreakdownOpen && (
                <div className="space-y-4">
                  {Object.entries(eventBreakdown).map(([cat, events]) => (
                    <div key={cat}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span
                          className="inline-block h-2 w-2 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: CATEGORY_COLORS[cat] ?? CATEGORY_COLORS.other }}
                        />
                        <span className="text-xs font-semibold text-gray-300 uppercase tracking-wide">
                          {cat.replace('_', ' ')}
                        </span>
                      </div>
                      <Table>
                        <thead>
                          <tr>
                            <Th>Event</Th>
                            <Th>Count</Th>
                            <Th>Avg Duration</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {events.map(ev => (
                            <tr key={ev.eventName} className="hover:bg-white/[0.02] transition-colors">
                              <Td><span className="font-mono text-xs text-gray-300">{ev.eventName}</span></Td>
                              <Td>{ev.count.toLocaleString()}</Td>
                              <Td className="text-gray-400">
                                {ev.avgDurationMs != null
                                  ? formatDuration(ev.avgDurationMs)
                                  : <span className="text-gray-600">—</span>}
                              </Td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Feedback tab content
// ---------------------------------------------------------------------------

function FeedbackTab() {
  const [filters, setFilters] = useState<FeedbackFilters>({
    days: 7,
    feedbackType: '',
    role: '',
    entityId: '',
  });
  const [isExporting, setIsExporting] = useState(false);

  const { data: summaryRaw, isLoading: summaryLoading } = useQuery<FeedbackSummaryData>({
    queryKey: ['feedback-summary', filters.days],
    queryFn: () => api.get<FeedbackSummaryData>(`/feedback/summary?days=${filters.days}`),
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = buildExportParams(filters);
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/feedback/export.csv?${params}`, { headers });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feedback-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <FeedbackFiltersBar
        value={filters}
        onChange={setFilters}
        onExport={handleExport}
        isExporting={isExporting}
      />
      <FeedbackSummary data={summaryRaw ?? null} isLoading={summaryLoading} />
      <FeedbackGrid filters={filters} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function Telemetry() {
  const [activeTab, setActiveTab] = useState<Tab>('analytics');

  return (
    <div>
      {/* Page header */}
      <PageHeader title="Analytics" subtitle="Platform-wide telemetry, feature adoption and user feedback" />

      {/* Tab bar */}
      <div className="flex border-b border-white/[0.08] mb-5">
        {TAB_LABELS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeTab === tab.id
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {tab.label}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'analytics' && <AnalyticsTab />}
      {activeTab === 'feedback' && <FeedbackTab />}
    </div>
  );
}
